import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, FolderOpen, Upload } from 'lucide-react';
import {
  analyzeVault,
  runImport,
  type ImportProgress,
  type ImportResult,
  type VaultAnalysis,
  type VaultFile,
} from '@nexus/workspace';
import { useStore } from '../db/context';
import { useUi } from '../state/ui';
import { cn } from '../lib/cn';
import { ModuleIcon } from './ModuleIcon';

type Step =
  | { kind: 'pick' }
  | { kind: 'analyzing' }
  | { kind: 'review'; analysis: VaultAnalysis }
  | { kind: 'running'; progress: ImportProgress }
  | { kind: 'done'; result: ImportResult }
  | { kind: 'error'; message: string };

/** Files from `<input webkitdirectory>`, with the chosen folder's own name stripped. */
function toVaultFiles(list: FileList): VaultFile[] {
  return [...list].map((f) => {
    const rel = (f.webkitRelativePath || f.name).replace(/\\/g, '/');
    const path = rel.includes('/') ? rel.slice(rel.indexOf('/') + 1) : rel;
    return { path, size: f.size, read: async () => new Uint8Array(await f.arrayBuffer()) };
  });
}

const CANONICAL = [
  'status',
  'priority',
  'due',
  'type',
  'category',
  'owner',
  'description',
  'tags',
  'aliases',
  'project',
];

const section = 'mt-6 rounded-xl border border-border p-4';
const h2 = 'mb-3 text-base font-semibold';

function Review({
  analysis,
  onStart,
}: {
  analysis: VaultAnalysis;
  onStart: (opts: Parameters<typeof runImport>[2]) => void;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const [mapping, setMapping] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(analysis.keys.map((k) => [k.key, k.suggested])),
  );
  const [includeConflicts, setIncludeConflicts] = useState(false);
  const [nested, setNested] = useState(false);
  const [dailyFolder, setDailyFolder] = useState(analysis.dailyFolder ?? '');
  const [templatesFolder, setTemplatesFolder] = useState(analysis.templatesFolder ?? '');
  const [tasksFolder, setTasksFolder] = useState(analysis.tasksFolder ?? '');
  const [aiExcluded, setAiExcluded] = useState<string[]>(analysis.sensitiveFolders);
  const [extraFolder, setExtraFolder] = useState('');
  const [previous, setPrevious] = useState<number | null>(null);
  const { i18n } = useTranslation();

  useEffect(() => {
    void store.getSetting('last_import_at').then((v) => setPrevious(v ? Number(v) : null));
  }, [store]);

  const s = analysis.stats;
  const counts: [string, number][] = [
    [t('import.notes'), analysis.notes.length - (includeConflicts ? 0 : analysis.conflicts.length)],
    [t('import.attachments'), analysis.attachments.length],
    [t('import.folders'), analysis.folders.length],
    [t('import.links'), s.wikilinks],
    [t('import.embeds'), s.embeds],
    [t('import.tasks'), s.tasks],
  ];

  return (
    <>
      {previous && (
        <div
          role="alert"
          className="mt-6 flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
        >
          <AlertTriangle className="size-4 shrink-0 text-amber-600" />
          {t('import.previous', {
            date: new Intl.DateTimeFormat(i18n.language, {
              dateStyle: 'medium',
              timeStyle: 'short',
            }).format(previous),
          })}
        </div>
      )}

      <dl className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-6">
        {counts.map(([label, n]) => (
          <div key={label} className="rounded-lg bg-bg-subtle p-3">
            <dt className="text-xs text-fg-muted">{label}</dt>
            <dd className="text-xl font-semibold tabular-nums">{n}</dd>
          </div>
        ))}
      </dl>

      <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-fg-muted">
        {(s.dataviewBlocks > 0 || s.tasksQueryBlocks > 0) && (
          <li>
            {t('import.queriesKept', { dataview: s.dataviewBlocks, tasks: s.tasksQueryBlocks })}
          </li>
        )}
        {s.rawBlocks > 0 && <li>{t('import.rawKept', { count: s.rawBlocks })}</li>}
        {analysis.skipped.length > 0 && (
          <li>{t('import.skipped', { count: analysis.skipped.length })}</li>
        )}
        {s.invalidFrontmatter > 0 && (
          <li>{t('import.invalidFrontmatter', { count: s.invalidFrontmatter })}</li>
        )}
        <li>{t('import.readOnly')}</li>
      </ul>

      {analysis.conflicts.length > 0 && (
        <section className={section}>
          <h2 className={h2}>{t('import.conflictsTitle', { count: analysis.conflicts.length })}</h2>
          <p className="text-sm text-fg-muted">{t('import.conflictsHelp')}</p>
          <details className="mt-2 text-xs text-fg-muted">
            <summary className="cursor-pointer">{t('import.showFiles')}</summary>
            <ul className="mt-1 space-y-0.5">
              {analysis.conflicts.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </details>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeConflicts}
              onChange={(e) => setIncludeConflicts(e.target.checked)}
            />
            {t('import.includeConflicts')}
          </label>
        </section>
      )}

      {analysis.keys.length > 0 && (
        <section className={section}>
          <h2 className={h2}>{t('import.mappingTitle')}</h2>
          <p className="mb-3 text-sm text-fg-muted">{t('import.mappingHelp')}</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-fg-muted">
                <th className="pb-1 font-medium">{t('import.inVault')}</th>
                <th className="pb-1 font-medium">{t('import.count')}</th>
                <th className="pb-1 font-medium">{t('import.inNexus')}</th>
              </tr>
            </thead>
            <tbody>
              {analysis.keys.map((k) => {
                const target = mapping[k.key];
                return (
                  <tr key={k.key} className="border-t border-border">
                    <td className="py-1 pr-2">
                      <code>{k.key}</code>
                    </td>
                    <td className="py-1 pr-2 tabular-nums text-fg-muted">{k.count}</td>
                    <td className="py-1">
                      <div className="flex items-center gap-2">
                        <input
                          aria-label={t('import.targetFor', { key: k.key })}
                          list="nx-canonical-keys"
                          disabled={target === null}
                          value={target ?? ''}
                          onChange={(e) => setMapping({ ...mapping, [k.key]: e.target.value })}
                          className={cn(
                            'w-40 rounded-md border border-border bg-bg px-2 py-0.5',
                            target !== k.key && target !== null && 'border-accent',
                          )}
                        />
                        <label className="flex items-center gap-1 text-xs text-fg-muted">
                          <input
                            type="checkbox"
                            checked={target === null}
                            onChange={(e) =>
                              setMapping({
                                ...mapping,
                                [k.key]: e.target.checked ? null : (k.suggested ?? k.key),
                              })
                            }
                          />
                          {t('import.skipKey')}
                        </label>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="nx-canonical-keys">
            {CANONICAL.map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
        </section>
      )}

      <section className={section}>
        <h2 className={h2}>{t('import.foldersTitle')}</h2>
        <div className="grid grid-cols-[10rem_1fr] items-center gap-3 text-sm">
          <label htmlFor="imp-daily">{t('import.dailyFolder')}</label>
          <select
            id="imp-daily"
            value={dailyFolder}
            onChange={(e) => setDailyFolder(e.target.value)}
            className="rounded-md border border-border bg-bg px-2 py-1"
          >
            <option value="">{t('import.none')}</option>
            {analysis.folders.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <label htmlFor="imp-tasks">{t('import.tasksFolder')}</label>
          <select
            id="imp-tasks"
            value={tasksFolder}
            onChange={(e) => setTasksFolder(e.target.value)}
            className="rounded-md border border-border bg-bg px-2 py-1"
          >
            <option value="">{t('import.none')}</option>
            {analysis.folders.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <label htmlFor="imp-templates">{t('import.templatesFolder')}</label>
          <select
            id="imp-templates"
            value={templatesFolder}
            onChange={(e) => setTemplatesFolder(e.target.value)}
            className="rounded-md border border-border bg-bg px-2 py-1"
          >
            <option value="">{t('import.none')}</option>
            {analysis.folders.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <span>{t('import.placement')}</span>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={nested} onChange={(e) => setNested(e.target.checked)} />
            {t('import.nested')}
          </label>
        </div>
      </section>

      <section className={section}>
        <h2 className={h2}>{t('import.aiTitle')}</h2>
        <p className="mb-2 text-sm text-fg-muted">{t('import.aiHelp')}</p>
        <ul className="space-y-1 text-sm">
          {[...new Set([...analysis.sensitiveFolders, ...aiExcluded])].map((f) => (
            <li key={f}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={aiExcluded.includes(f)}
                  onChange={(e) =>
                    setAiExcluded(
                      e.target.checked ? [...aiExcluded, f] : aiExcluded.filter((x) => x !== f),
                    )
                  }
                />
                {f}
              </label>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex items-center gap-2 text-sm">
          <select
            aria-label={t('import.addFolder')}
            value={extraFolder}
            onChange={(e) => setExtraFolder(e.target.value)}
            className="min-w-0 flex-1 rounded-md border border-border bg-bg px-2 py-1"
          >
            <option value="">{t('import.addFolder')}</option>
            {analysis.folders
              .filter((f) => !aiExcluded.includes(f))
              .map((f) => (
                <option key={f}>{f}</option>
              ))}
          </select>
          <button
            type="button"
            disabled={!extraFolder}
            onClick={() => {
              setAiExcluded([...aiExcluded, extraFolder]);
              setExtraFolder('');
            }}
            className="rounded-md px-2 py-1 hover:bg-bg-hover disabled:opacity-40"
          >
            {t('import.add')}
          </button>
        </div>
      </section>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={() =>
            onStart({
              mapping: Object.fromEntries(
                Object.entries(mapping).map(([k, v]) => [k, v === null ? null : v.trim() || k]),
              ),
              includeConflicts,
              rootTitle: nested ? t('import.rootTitle') : null,
              aiExcludedFolders: aiExcluded,
              dailyFolder: dailyFolder || null,
              templatesFolder: templatesFolder || null,
              tasksFolder: tasksFolder || null,
            })
          }
          className="flex items-center gap-2 rounded-full bg-accent px-4 py-2 font-medium text-accent-fg"
        >
          <Upload className="size-4" />
          {t('import.start')}
        </button>
      </div>
    </>
  );
}

export function ImportView() {
  const { t } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const [step, setStep] = useState<Step>({ kind: 'pick' });
  const input = useRef<HTMLInputElement>(null);

  // `webkitdirectory` is not in React's typings; set it on the element.
  useEffect(() => {
    input.current?.setAttribute('webkitdirectory', '');
  }, [step.kind]);

  const phaseLabel = useMemo(() => (p: ImportProgress) => t(`import.phase.${p.phase}`), [t]);

  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    setStep({ kind: 'analyzing' });
    try {
      setStep({ kind: 'review', analysis: await analyzeVault(toVaultFiles(files)) });
    } catch (e) {
      setStep({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  };

  const start = async (analysis: VaultAnalysis, opts: Parameters<typeof runImport>[2]) => {
    setStep({ kind: 'running', progress: { phase: 'folders', done: 0, total: 1 } });
    try {
      const result = await runImport(store, analysis, opts, (progress) =>
        setStep({ kind: 'running', progress }),
      );
      setStep({ kind: 'done', result });
    } catch (e) {
      setStep({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  };

  return (
    <section className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10">
      <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
        <ModuleIcon kind="import" size="lg" />
        {t('import.title')}
      </h1>
      <p className="mt-2 text-fg-muted">{t('import.intro')}</p>

      {(step.kind === 'pick' || step.kind === 'error') && (
        <div className="mt-8">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-accent px-4 py-2 font-medium text-accent-fg focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--focus)]">
            <FolderOpen className="size-4" />
            {t('import.choose')}
            <input
              ref={input}
              type="file"
              multiple
              className="sr-only"
              aria-label={t('import.choose')}
              onChange={(e) => void pick(e.target.files)}
            />
          </label>
          {step.kind === 'error' && (
            <p role="alert" className="mt-4 text-sm text-danger">
              {t('import.failed')}: {step.message}
            </p>
          )}
        </div>
      )}

      {step.kind === 'analyzing' && <p className="mt-8 text-fg-muted">{t('import.analyzing')}</p>}

      {step.kind === 'review' && (
        <Review analysis={step.analysis} onStart={(o) => void start(step.analysis, o)} />
      )}

      {step.kind === 'running' && (
        <div className="mt-8" role="status">
          <p className="mb-2 text-sm">
            {phaseLabel(step.progress)} — {step.progress.done} / {step.progress.total}
          </p>
          <progress
            className="h-2 w-full accent-[var(--accent)]"
            value={step.progress.done}
            max={step.progress.total}
            aria-label={t('import.progress')}
          />
        </div>
      )}

      {step.kind === 'done' && (
        <div className="mt-8 rounded-xl border border-border p-4" role="status">
          <p className="flex items-center gap-2 font-medium">
            <CheckCircle2 className="size-5 text-green-600" />
            {t('import.done', { pages: step.result.pages, attachments: step.result.attachments })}
          </p>
          {step.result.unresolvedLinks > 0 && (
            <p className="mt-2 text-sm text-fg-muted">
              {t('import.unresolved', { count: step.result.unresolvedLinks })}
            </p>
          )}
          {step.result.warnings.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-sm text-fg-muted">
              {step.result.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          {step.result.rootPageId && (
            <button
              type="button"
              onClick={() => open({ kind: 'page', pageId: step.result.rootPageId! })}
              className="mt-3 rounded-md px-3 py-1.5 text-sm hover:bg-bg-hover"
            >
              {t('import.openRoot')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
